import { useCallback, useContext } from "react";
import { graphql } from "react-relay";

import * as Sentry from "@sentry/browser";
import { fetchQuery } from "relay-runtime";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import OngoingMeetingContext from "~/OngoingMeetingContext";

import type {
	useSaveEventMutationMutation as SaveEventMutation,
	useSaveEventMutationMutation$variables as SaveEventMutationVariables,
	useSaveEventMutationMutation$data,
} from "~/__generated__/useSaveEventMutationMutation.graphql";

export const saveEventMutation = graphql`
	mutation useSaveEventMutationMutation(
		$calendar: String!
		$title: String!
		$start: DateTime!
		$end: DateTime!
		$repeat: repeat
		$description: String
		$isAllDay: Boolean
		$attendees: [String]
		$optionalAttendees: [String]
		$conferenceProvider: conference_provider
		$googleMeetId: String
		$editType: edit_type
		$guid: String
		$captchaToken: String!
		$streamDescription: String
		$sendNotifications: Boolean
		$customRrule: CustomRecurringRuleSerializerInput
		$eventColor: fellow_color
		$schedulingAutomationIsActive: Boolean
		$conferenceCallUrl: String
	) {
		createOrUpdateEvent(
			input: {
				calendar: $calendar
				title: $title
				start: $start
				end: $end
				repeat: $repeat
				customRrule: $customRrule
				description: $description
				isAllDay: $isAllDay
				attendeesList: $attendees
				optionalAttendeesList: $optionalAttendees
				conferenceProvider: $conferenceProvider
				editType: $editType
				googleMeetId: $googleMeetId
				guid: $guid
				captchaToken: $captchaToken
				streamDescription: $streamDescription
				sendNotifications: $sendNotifications
				fellowColor: $eventColor
				schedulingAutomationIsActive: $schedulingAutomationIsActive
				conferenceCallUrl: $conferenceCallUrl
			}
		) {
			guid
			title
			start
			end
			isAllDay
			errors {
				field
				messages
			}
		}
	}
`;

export const TITLE_MAX_LENGTH = 1024;

export type SavedEvent = Omit<
	NonNullable<useSaveEventMutationMutation$data["createOrUpdateEvent"]>,
	"start" | "end"
> & {
	start: Date;
	end: Date;
	calendar: string;
	isCreated: boolean;
	warnings?: string[];
};

export type SaveEventMutationError = {
	name: "title-too-long" | "no-title" | "unknown-error";
	message: string;
};

function refetchMeetingAssistantAutomationValue(guid: string) {
	fetchQuery(
		environment,
		graphql`
			query useSaveEventMutationRefetchMeetingAssistantAutomationValueQuery($guid: String!) {
				event: eventByGuid(guid: $guid) {
					... on Event {
						stream {
							schedulingMeetingAssistantAutomationIsActive
						}
					}
				}
			}
		`,
		{ guid },
		{ fetchPolicy: "network-only" },
	).toPromise();
}

export default function useSaveEventMutation() {
	const { ongoingMeeting, dismissPrompt } = useContext(OngoingMeetingContext);

	return useCallback(
		async (variables: SaveEventMutationVariables): Promise<SavedEvent | SaveEventMutationError> => {
			try {
				if (variables.title.length >= TITLE_MAX_LENGTH) {
					return {
						name: "title-too-long",
						message: `Title must be fewer than ${TITLE_MAX_LENGTH} characters.`,
					};
				}

				if (variables.title.length === 0) {
					return {
						name: "no-title",
						message: `Please give your event a title.`,
					};
				}

				const response = await commitMutationPromise<SaveEventMutation>(environment, {
					mutation: saveEventMutation,
					variables,
				});

				const result = response.createOrUpdateEvent;

				if (!result || !result.guid) {
					return {
						name: "unknown-error",
						message: "Improper response from server after saving event",
					};
				}

				let warnings: string[] | undefined = undefined;

				if (response.createOrUpdateEvent?.errors?.length) {
					Sentry.captureException("Predictable error while saving event", {
						extra: {
							errors: response.createOrUpdateEvent.errors,
						},
					});

					warnings = response.createOrUpdateEvent.errors.map(error => error?.messages || []).flat();
				}

				if (variables.schedulingAutomationIsActive !== undefined) {
					refetchMeetingAssistantAutomationValue(result.guid);
				}

				if (ongoingMeeting?.guid == result.guid) {
					// Ignoring the possibility that the event could still be ongoing, and dismissing the prompt.
					dismissPrompt();
				}

				return {
					...result,
					guid: result.guid,
					start: result.start ? new Date(result.start) : new Date(),
					end: result.end ? new Date(result.end) : new Date(),

					calendar: variables.calendar,
					isCreated: !variables.guid,
					warnings,
				};
			} catch (errors) {
				Sentry.captureException("Unknown error while saving event", {
					extra: {
						errors,
					},
				});

				return {
					name: "unknown-error",
					message: "Something went wrong while saving the event. If this persists, contact support.",
				};
			}
		},
		[ongoingMeeting, dismissPrompt],
	);
}
